import React, { useState } from 'react';
import { t } from 'i18next';
import { Menu, MenuItem } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import DataTable, { ColDef } from '../util/DataTable';
import EmptyState from '../util/EmptyState';
import UserAvatar from '../util/UserAvatar';

interface PayrollTableProps {
  users: any[];
  handleShow: (user: any) => void;
  selectedContractTypeFilter: number | null;
  access: any;
  baseUrl: string;
  contractType: any[];
  refreshUsers: () => void;
  toolbarRef?: React.RefObject<HTMLElement>;
}

const PayrollTable: React.FC<PayrollTableProps> = ({
  users, handleShow, selectedContractTypeFilter, access, baseUrl, contractType, refreshUsers, toolbarRef,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<any>(null);

  const handleContractTypeChange = async (contractId: any) => {
    try {
      await axios.put(`update-user/${selectedUserId}`, { patternId: contractId });
      refreshUsers();
    } finally {
      setMenuAnchor(null);
      setSelectedUserId(null);
    }
  };

  const filteredUsers = selectedContractTypeFilter
    ? users.filter((u: any) => u.patternId === selectedContractTypeFilter)
    : users;

  const columns: ColDef<any>[] = [
    {
      key: 'name',
      label: t('Name'),
      sortExtract: r => r.first_name ?? '',
      render: r => (
        <div className="flex gap-[10px] items-center cursor-pointer" onClick={() => handleShow(r)}>
          <UserAvatar user={r} baseUrl={baseUrl} size={30} />
          <div className="flex gap-[5px] items-center flex-wrap">
            <span className="user-box-name">{r.first_name}</span>
            <span className="user-box last">{r.last_name}</span>
            {r.second_name && <span className="user-box second">{r.second_name}</span>}
          </div>
        </div>
      ),
    },
    {
      key: 'pattern',
      label: t('HrWorkingPattern'),
      sortExtract: r => r.pattern?.name ?? '',
      render: r => (
        <span className="cursor-pointer hover:opacity-70"
          onClick={(e: any) => { setMenuAnchor(e.currentTarget); setSelectedUserId(r.id); }}>
          {r.pattern?.name ?? '—'}
        </span>
      ),
    },
    {
      key: 'contract_start_date',
      label: t('ContractStartDate'),
      sortExtract: r => r.contract_start_date ?? '',
      render: r => r.contract_start_date ? moment(r.contract_start_date).format('DD.MM.YYYY') : '—',
    },
    {
      key: 'hoursPerWeek',
      label: t('PerWeek'),
      render: r => r.pattern?.hoursPerWeek ?? 0,
    },
    {
      key: 'salary_perHour',
      label: t('CostPerHour'),
      render: r => r.salary_perHour ?? 0,
    },
  ];

  return (
    <>
      <DataTable
        rows={filteredUsers}
        getKey={r => r.id}
        columns={columns}
        onEdit={(access?.payroll_edit === true || access?.payroll_edit === 1) ? handleShow : undefined}
        pageSizes={[10, 20, 50]}
        defaultPageSize={50}
        emptyText={t('No users available') as string}
        toolbarRef={toolbarRef}
      />
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {contractType.map((c: any) => (
          <MenuItem key={c.id} onClick={() => handleContractTypeChange(c.id)}>{c.name}</MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default PayrollTable;
